import { Box, Link, Typography } from '@customink/pigment-react';
import type { SxProps } from '@mui/system/styleFunctionSx';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import chatBubble from '@/assets/chatBubble.svg';
import { rollbar } from '@/config/rollbar.config';

type LiveEngageProps = {
  children?: ReactNode;
  showChatIcon?: boolean;
  liveEngageId: string;
  onClick?: () => void;
  sx?: SxProps;
  prefix?: string;
  suffix?: string;
};

/* TODO: Even better would be to import and use the LivePerson SDK
 * https://developers.liveperson.com/messaging-platform-sdk-overview.html
 * but it isn't exactly a drop-in replacement */
const LiveEngage = ({
  liveEngageId,
  onClick,
  sx,
  showChatIcon,
  children,
  prefix,
  suffix,
}: LiveEngageProps) => {
  const [livePersonContainerElem, setLivePersonContainerElem] =
    useState<HTMLSpanElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [innerHTML, setInnerHTML] = useState<string>('');

  useEffect(() => {
    if (!livePersonContainerElem) {
      const liveEngageElement = document.getElementById(liveEngageId);
      liveEngageElement &&
        setLivePersonContainerElem(liveEngageElement as HTMLSpanElement);
    }
  }, [liveEngageId, livePersonContainerElem]);

  useEffect(() => {
    if (!livePersonContainerElem) return;

    if (livePersonContainerElem.hasChildNodes()) {
      setInnerHTML(livePersonContainerElem.innerHTML);
      setIsLoading(false);
      return;
    }

    const timeoutHandler = setTimeout(
      () => rollbar.warn('liveperson chat failed to load after 5 seconds'),
      5000,
    );

    const observer = new MutationObserver(() => {
      if (livePersonContainerElem.innerHTML) {
        setInnerHTML(livePersonContainerElem.innerHTML);
      }
      setIsLoading(false);
      if (timeoutHandler) clearTimeout(timeoutHandler);
    });

    observer.observe(livePersonContainerElem, { childList: true });

    return () => {
      if (observer) observer?.disconnect();
      if (timeoutHandler) clearTimeout(timeoutHandler);
    };
  }, [livePersonContainerElem]);

  const handleClick = useCallback(() => {
    if (!isLoading && livePersonContainerElem) {
      /* NOTE: This is the most brittle part of this implementation, so look
       * here and consider modifying this selector if chat ever breaks */
      const livePersonButton = livePersonContainerElem.querySelector(
        '[data-lp-event=click]',
      ) as HTMLButtonElement;
      onClick?.();
      livePersonButton.click();
    }
  }, [isLoading, livePersonContainerElem, onClick]);

  if (isLoading) return null;

  return (
    <Box
      sx={{
        ...{ mr: 'auto', display: 'inline-flex', alignItems: 'center' },
        ...sx,
      }}
    >
      {prefix && <Typography component="span">{prefix}&nbsp;</Typography>}
      <Box
        onClick={handleClick}
        sx={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {showChatIcon && (
          <img
            alt="chat with a representative"
            src={chatBubble}
            style={{
              height: '24px',
              width: '24px',
              cursor: 'pointer',
              paddingRight: '8px',
            }}
          />
        )}
        {children ? (
          children
        ) : (
          <Box dangerouslySetInnerHTML={{ __html: innerHTML }} />
        )}
      </Box>
      {suffix && <Typography component="span">&nbsp;{suffix}</Typography>}
    </Box>
  );
};

type LiveEngageLinkWithLinkProps = LiveEngageProps;

const LiveEngageLink: React.FC<LiveEngageLinkWithLinkProps> = ({
  children,
  ...props
}) => (
  <LiveEngage {...props}>
    <Link button style={{ textDecoration: 'none', verticalAlign: 'top' }}>
      {children || 'Chat with Us'}
    </Link>
  </LiveEngage>
);

export { LiveEngage, LiveEngageLink };
